import React, { useEffect, useState, Suspense, lazy } from "react";
import "./App.css";
import { BrowserRouter, Routes, Route, useLocation, useNavigate, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider } from "./lib/auth";
import AppShell from "./components/AppShell";
import Splash from "./components/Splash";
import Onboarding from "./components/Onboarding";
import AuthCallback from "./components/AuthCallback";
import RuneCelebrator from "./components/RuneCelebrator";
import Discover from "./pages/Discover";
// MapPage is code-split — Leaflet + react-leaflet + cluster libs are heavy
// (~120 KB gzipped) and only need to load when the walker taps the Map tab.
const MapPage = lazy(() => import("./pages/MapPage"));
import LocationDetail from "./pages/LocationDetail";
import Favourites from "./pages/Favourites";
import Profile from "./pages/Profile";
import About from "./pages/About";
import Gear from "./pages/Gear";
import AdminTelemetry from "./pages/AdminTelemetry";
import AdminUsers from "./pages/AdminUsers";
import AdminReports from "./pages/AdminReports";
import AdminAddTrail from "./pages/AdminAddTrail";
import AdminModeration from "./pages/AdminModeration";
import AdminLocations from "./pages/AdminLocations";
import AdminAddBasecamp from "./pages/AdminAddBasecamp";
import AdminEditBasecamp from "./pages/AdminEditBasecamp";
import BasecampDetail from "./pages/BasecampDetail";
import EmailAuth from "./components/EmailAuth";

function WelcomeGate() {
  const navigate = useNavigate();
  const always = localStorage.getItem("td_always_show_welcome") === "true";
  const seen = localStorage.getItem("td_onboarded") === "true";
  useEffect(() => {
    if (seen && !always) navigate("/discover", { replace: true });
  }, [seen, always, navigate]);
  return <Splash onBegin={() => navigate("/onboarding")} />;
}

function AuthPage() {
  const navigate = useNavigate();
  return (
    <div className="min-h-screen max-w-md mx-auto px-6 py-10 flex flex-col">
      <EmailAuth onSignedIn={() => navigate("/discover")} />
    </div>
  );
}

function Router() {
  const location = useLocation();
  // Handle Emergent OAuth fragment regardless of path
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      <Route path="/" element={<WelcomeGate />} />
      <Route path="/onboarding" element={<Onboarding />} />
      <Route path="/auth" element={<AuthPage />} />
      <Route path="/discover" element={<Discover />} />
      <Route path="/map" element={
        <Suspense fallback={<div className="p-8 text-brand-subtle">Loading map...</div>}>
          <MapPage />
        </Suspense>
      } />
      <Route path="/location/:id" element={<LocationDetail />} />
      <Route path="/basecamp/:id" element={<BasecampDetail />} />
      <Route path="/favourites" element={<Favourites />} />
      <Route path="/about" element={<About />} />
      <Route path="/gear" element={<Gear />} />
      <Route path="/profile" element={<Profile />} />
      <Route path="/admin/telemetry" element={<AdminTelemetry />} />
      <Route path="/admin/users" element={<AdminUsers />} />
      <Route path="/admin/reports" element={<AdminReports />} />
      <Route path="/admin/trails/new" element={<AdminAddTrail />} />
      <Route path="/admin/basecamps/new" element={<AdminAddBasecamp />} />
      <Route path="/admin/basecamps/:id" element={<AdminEditBasecamp />} />
      <Route path="/admin/moderation" element={<AdminModeration />} />
      <Route path="/admin/locations" element={<AdminLocations />} />
      <Route path="*" element={<Navigate to="/discover" replace />} />
    </Routes>
  );
}

function App() {
  // Privacy — on every app open, drop any recorder session that's
  // either malformed or older than 24 h so stale raw fixes never
  // linger on disk after a crash / abandon. See useWalkRecorder.
  useEffect(() => {
    import("./lib/useWalkRecorder").then((m) => m.purgeStaleWalk?.());
  }, []);
  return (
    <AuthProvider>
      <BrowserRouter>
        <AppShell>
          <Router />
        </AppShell>
        <RuneCelebrator />
        <Toaster richColors position="top-center" toastOptions={{ style: { fontFamily: "DM Sans, sans-serif" } }} />
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
